import { Employee } from "@finway-group/shared/lib/models"
import { Substitute } from "@finway-group/shared/lib/models/user/Substitute"
import { Tooltip } from "antd"
import React, { useCallback } from "react"
import { Briefcase as BriefcaseIcon, Edit as EditIcon, Globe as GlobeIcon, PlusCircle } from "react-feather"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import Icon from "~Components/icon"
import { EmployeeAbsenceModal } from "~Components/modals/employeeAbsence.modal"
import { SubstituteModal } from "~Components/modals/substitute.modal"
import UserImage from "~Components/UserImage"
import SettingsLayout, { SettingsButton } from "~Features/settings/settings.layout"
import { useModal } from "~Shared/context/modal.context"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useEmployees, useIsUserAbsenceBeingSynced } from "~Shared/hooks/employee.hooks"
import { useLoggedInEmployeeProfile } from "~Shared/queries/employee.queries"
import { employeeAbsenceInfo } from "~Shared/utils/employee.utils"

export const AbsenceManagementSettings = () => {
    const { t } = useTranslation()
    const employee = useLoggedInEmployeeProfile()
    const { showModal } = useModal()
    const { _id: companyId } = useCompany()
    const substitutes = employee?.activeCompanyProfile?.substitutes
    const { isAbsenceBeingSynced, loading: isAbsenceBeingSyncedLoading } = useIsUserAbsenceBeingSynced(employee)

    const employees = useEmployees({ excludeDeleted: true })
    const { substitutes: verifiedSubstitutes, employees: verifiedEmployees } = verifiedExistingEmployeeSubstitutes(substitutes, employees)
    const setupAbsence = useCallback(() => {
        if (!employee || isAbsenceBeingSynced) {
            return
        }
        showModal(EmployeeAbsenceModal, true, { isShowing: true, employee })
    }, [showModal, employee, isAbsenceBeingSynced])

    const showSubstituteModal = useCallback(() => {
        if (!employee) {
            return
        }
        showModal(SubstituteModal, true, { isShowing: true, companyId, existingSubstitutes: verifiedSubstitutes })
    }, [showModal, companyId, verifiedSubstitutes, employee])
    const employeeAbsenceMessage = employee ? employeeAbsenceInfo(employee, t) : undefined

    return (
        <SettingsLayout titleKey="label:employee_absence.absence">
            <ul className="description-list">
                <li className="items-center">
                    <div className="flex flex-row items-center">
                        <Tooltip title={t("tooltips:setting_scope.global")}>
                            <GlobeIcon className="mr-10 flex-shrink-0" size={16} />
                        </Tooltip>
                        <span className="title">{t("action:employee_absence:set_up")}</span>
                    </div>
                    {employeeAbsenceMessage ? (
                        <div className="flex items-center">
                            <span>{employeeAbsenceMessage}</span>
                            {!isAbsenceBeingSynced && <EditIcon className="cursor-pointer ml-10" size={14} onClick={setupAbsence} />}
                        </div>
                    ) : (
                        <Tooltip title={isAbsenceBeingSynced ? t("action:employee_absence.tooltip") : undefined}>
                            <div>
                                <SettingsButton
                                    icon={<Icon className="pb-2 pr-6" color="#fff" icon="calendar_cancel" />}
                                    onClick={setupAbsence}
                                    labelKey="action:employee_absence.add_absence"
                                    disabled={isAbsenceBeingSynced}
                                    loading={isAbsenceBeingSyncedLoading}
                                />
                            </div>
                        </Tooltip>
                    )}
                </li>
                <li className="items-center">
                    <div className="flex flex-row items-center">
                        <Tooltip title={t("tooltips:setting_scope.company")}>
                            <BriefcaseIcon className="mr-10 flex-shrink-0" size={16} />
                        </Tooltip>
                        <span className="title">{t("label:employee_absence:substitute_for_approval")}</span>
                    </div>
                    {verifiedEmployees.length === 0 ? (
                        <SettingsButton icon={<PlusCircle size={20} className="mb-2" />} onClick={showSubstituteModal} labelKey="action:employee_absence.add_substitute" />
                    ) : (
                        <div className="flex flex-wrap items-center">
                            {verifiedEmployees.map((employee) => (
                                <Link key={employee.id} to={`/employees/${employee.id}`}>
                                    <span className="flex items-center mr-10">
                                        <UserImage key={employee.id} user={employee} />
                                        <span className="truncate max-w-200"> {employee.getFullName()}</span>
                                    </span>
                                </Link>
                            ))}
                            <EditIcon className="cursor-pointer flex-shrink-0" size={14} onClick={showSubstituteModal} />
                        </div>
                    )}
                </li>
            </ul>
        </SettingsLayout>
    )
}

const verifiedExistingEmployeeSubstitutes = (substitutes: Array<Substitute> | undefined, employees: Array<Employee>) => {
    if (!substitutes) {
        return { substitutes, employees: [] }
    }
    const verifiedEmployees: Array<Employee> = substitutes
        .map((substituteItem) => employees.find((employeeItem) => employeeItem.id === substituteItem.userId))
        .filter(Boolean) as Array<Employee>
    const verifiedSubstitutes = verifiedEmployees.map((employeeItem) => ({
        userId: employeeItem?.id,
    }))

    return { substitutes: verifiedSubstitutes, employees: verifiedEmployees }
}
